import type { SupabaseClient } from "@supabase/supabase-js";
import { publicReadForViewer } from "@/lib/public-catalog-columns";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/types/database";
import { Track } from "@/types/track";

/**
 * Version families for the PUBLISHED catalog.
 *
 * The drafts equivalent (`draft-versions.ts`) cannot be reused: it hardcodes
 * `is_published: false` and returns `Draft` objects via the draft mapper. This is the
 * published-side parallel — same batching strategy, different shape.
 *
 * Catalog queries fetch main works only (`parent_track_id IS NULL`), which removes 8,322 of
 * Felt's 13,137 published tracks from the grid. Children are fetched once per page of parents,
 * so a page of 40 parents costs exactly one extra query and expanding a family is instant.
 *
 * The hierarchy is exactly two levels deep — no version points at another version — so this
 * never recurses.
 */

/** Page size is well under this; it exists so a large caller cannot blow the URL length. */
const MAX_PARENTS_PER_QUERY = 200;

export interface TrackVersions {
  /** parent track id -> its version rows, ordered for display */
  byParent: Map<string, Track[]>;
  /** parent track id -> child count */
  counts: Map<string, number>;
}

export const EMPTY_TRACK_VERSIONS: TrackVersions = {
  byParent: new Map(),
  counts: new Map(),
};

/** Full length first, then progressively shorter edits — the order a person reads a family. */
const EDIT_RANK: Record<string, number> = {
  "Full Length": 0,
  "60 Second": 1,
  "30 Second": 2,
  "20 Second": 3,
  "15 Second": 4,
  "10 Second": 5,
  Ident: 6,
  Stab: 7,
};

function compareVersions(a: Track, b: Track): number {
  const ra = EDIT_RANK[a.edit_type ?? ""] ?? 50;
  const rb = EDIT_RANK[b.edit_type ?? ""] ?? 50;
  if (ra !== rb) return ra - rb;
  const va = (a.version_type ?? "").localeCompare(b.version_type ?? "");
  if (va !== 0) return va;
  return (a.title ?? "").localeCompare(b.title ?? "");
}

/**
 * Fetch every PUBLISHED version belonging to the given parents.
 *
 * Constrained to `is_published = true` so an unpublished draft version is never surfaced on a
 * buyer-facing catalog surface — the sheet is mounted from 23 call sites, several of them
 * public, so this filter is load-bearing rather than cosmetic.
 *
 * Returns empty rather than throwing: a failure here must degrade the version badge, never
 * blank the catalog.
 */
export async function fetchVersionsForTracks(
  parentIds: string[]
): Promise<TrackVersions> {
  const ids = [...new Set(parentIds.filter(Boolean))];
  if (ids.length === 0) return EMPTY_TRACK_VERSIONS;

  const byParent = new Map<string, Track[]>();
  const counts = new Map<string, number>();

  try {
    for (let i = 0; i < ids.length; i += MAX_PARENTS_PER_QUERY) {
      const slice = ids.slice(i, i + MAX_PARENTS_PER_QUERY);
      const { data, error } = await supabase
        .from("tracks")
        .select("*")
        .in("parent_track_id", slice)
        .eq("is_published", true);

      if (error) {
        console.error("Failed to fetch track versions:", error);
        continue; // a partial family beats no catalog
      }

      // Same boundary assertion the catalog store makes: rows come back as the tracks
      // table shape and are consumed as the app Track view.
      for (const row of (data || []) as unknown as Track[]) {
        const parentId = row.parent_track_id;
        if (!parentId) continue;
        const bucket = byParent.get(parentId);
        if (bucket) bucket.push(row);
        else byParent.set(parentId, [row]);
      }
    }

    for (const [parentId, children] of byParent) {
      children.sort(compareVersions);
      counts.set(parentId, children.length);
    }
  } catch (err) {
    console.error("Unexpected error fetching track versions:", err);
    return EMPTY_TRACK_VERSIONS;
  }

  return { byParent, counts };
}

/**
 * Attach fetched children to their parents. Parents with no versions get `version_count: 0`
 * so the UI can tell "no versions" apart from "not loaded yet".
 */
export function attachTrackVersions(
  parents: Track[],
  versions: TrackVersions
): Track[] {
  return parents.map((parent) => {
    const children = versions.byParent.get(String(parent.id));
    return {
      ...parent,
      version_count: versions.counts.get(String(parent.id)) ?? 0,
      versions: children ?? [],
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// The shared track page (/track/shared/:trackId) — public, read by signed-out visitors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every column the shared track page's Versions tab reads, and nothing else: what a row shows
 * (version_type, edit_type, title, duration), what Play hands the app player (id, title, artist,
 * genre, artwork), and the family link. The page is public, and a `tracks` row also carries file
 * names, uploader ids, analysis errors and embedded tag dumps that a recipient has no use for.
 */
export const PUBLIC_FAMILY_COLUMNS =
  "id, parent_track_id, title, artist, genre, version_type, edit_type, duration, artwork_url, cover_art_url";

/** `tracks.id` is a uuid. Anything else is not a track, and must not reach the `or` filter below. */
const TRACK_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PublishedFamily {
  /**
   * The main work, or null when it is still a draft while some of its versions are published.
   * The page then lists those versions as siblings, without a Main row.
   */
  main: Track | null;
  /** The work's published versions, full length first. Never empty. */
  versions: Track[];
}

/** How many published tracks the family holds: the main work (when published) and its versions. */
export function publishedFamilySize(family: PublishedFamily): number {
  return (family.main ? 1 : 0) + family.versions.length;
}

/** A main work and its published versions, in one read. `mainId` is a validated uuid. */
async function readPublishedFamily(
  client: SupabaseClient<Database>,
  mainId: string
): Promise<Track[]> {
  const { data, error } = await client
    .from("tracks")
    .select(PUBLIC_FAMILY_COLUMNS)
    .or(`id.eq.${mainId},parent_track_id.eq.${mainId}`)
    .eq("is_published", true);
  if (error) throw error;
  // Only PUBLIC_FAMILY_COLUMNS are populated; asserted to the app Track view at the boundary,
  // the same way the catalog store does.
  return (data ?? []) as unknown as Track[];
}

/**
 * The published family that `trackId` belongs to, for the public shared track page and the track
 * details panel's Versions tab (`usePublishedFamily`).
 *
 * A shared link can point at a main work or at one of its versions; either way the answer is the
 * main work and every published version of it. Usually one read; a link to a version costs a
 * second one, for its main work's family.
 *
 * When the main work is still a draft but the shared version is published, the answer is the
 * work's published versions with `main: null` — they are live, so the recipient sees them.
 *
 * Null when there is nothing to show: the id is not a published track, or nothing else in the
 * work is published (a main work without published versions, or a version under a draft main
 * work with no published siblings). Throws when a read fails; the page decides what a failure
 * looks like.
 */
export async function fetchPublishedFamily(
  trackId: string
): Promise<PublishedFamily | null> {
  if (!TRACK_ID_PATTERN.test(trackId)) return null;
  const sharedId = trackId.toLowerCase();

  // The columns are public either way. The client is not: a signed-in account that is not fully
  // set up reads no `tracks` rows through its own session (backend migration
  // 20261013055000_set_up_accounts_only.sql), so the signed-out view reads through the anon-only
  // client, as a signed-out visitor does. A set-up account reads through its own, as before.
  const { client } = await publicReadForViewer();

  let rows = await readPublishedFamily(client, sharedId);
  const shared = rows.find((row) => String(row.id) === sharedId);
  if (!shared) return null;

  const mainId = shared.parent_track_id ?? sharedId;
  if (mainId !== sharedId) rows = await readPublishedFamily(client, mainId);

  const versions = rows
    .filter((row) => row.parent_track_id === mainId)
    .sort(compareVersions);
  const main = rows.find((row) => String(row.id) === mainId);
  if (main) return versions.length > 0 ? { main, versions } : null;

  // The main work is a draft (the is_published filter leaves it out): list the published
  // versions, as long as the shared one has at least one published sibling.
  return versions.some((row) => String(row.id) !== sharedId)
    ? { main: null, versions }
    : null;
}
